import React, { FC, useState, useEffect } from "react"
import { useRouter } from "next/router"
import { Button } from "components/core/button"
import { InputLabel } from "components/core/inputLabel"
import { SEO } from "components/core/seo"
import { Alert } from "components/core/alert"
import { useSignIn, useSignInWithOAuth, useUser } from "lib/supabase/auth"
import { Container } from "components/core/container"
import { Card } from "components/core/card"
import { Link } from "components/core/link"
import { BASE_URL } from "lib/utils"

const LoginPage: FC = () => {
  const { mutateAsync: signIn, isLoading, error } = useSignIn()
  const { mutateAsync: signInWithOAuth, error: isSignInWithOAuthError } = useSignInWithOAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const { user } = useUser()
  const router = useRouter()

  useEffect(() => {
    if (user) {
      router.push("/dashboard")
    }
  }, [router, user])

  return (
    <Container small>
      <SEO
        title="Sign in"
        description="Sign in to your account"
        ogImage={{
          title: "Sign in",
          url: `${BASE_URL}/auth/login`,
        }}
      />

      <Card className="p-8 rounded-md lg:rounded-xl">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-200 text-center">Sign in to your account</h1>

        {(error || isSignInWithOAuthError) && (
          <Alert type="error" className="mt-6">
            {(error || isSignInWithOAuthError)?.message}
          </Alert>
        )}

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <Button
            size="big"
            type="button"
            className="justify-center"
            onClick={() => signInWithOAuth({ provider: "google" })}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="h-5">
              <path
                style={{
                  fill: "#fbbb00",
                }}
                d="M113.47 309.408L95.648 375.94l-65.139 1.378C11.042 341.211 0 299.9 0 256c0-42.451 10.324-82.483 28.624-117.732h.014L86.63 148.9l25.404 57.644c-5.317 15.501-8.215 32.141-8.215 49.456.002 18.792 3.406 36.797 9.651 53.408z"
              />
              <path
                style={{
                  fill: "#518ef8",
                }}
                d="M507.527 208.176C510.467 223.662 512 239.655 512 256c0 18.328-1.927 36.206-5.598 53.451-12.462 58.683-45.025 109.925-90.134 146.187l-.014-.014-73.044-3.727-10.338-64.535c29.932-17.554 53.324-45.025 65.646-77.911h-136.89V208.176h245.899z"
              />
              <path
                style={{
                  fill: "#28b446",
                }}
                d="M416.253 455.624l.014.014C372.396 490.901 316.666 512 256 512c-97.491 0-182.252-54.491-225.491-134.681l82.961-67.91c21.619 57.698 77.278 98.771 142.53 98.771 28.047 0 54.323-7.582 76.87-20.818l83.383 68.262z"
              />
              <path
                style={{
                  fill: "#f14336",
                }}
                d="M419.404 58.936l-82.933 67.896C313.136 112.246 285.552 103.82 256 103.82c-66.729 0-123.429 42.957-143.965 102.724l-83.397-68.276h-.014C71.23 56.123 157.06 0 256 0c62.115 0 119.068 22.126 163.404 58.936z"
              />
            </svg>
            <span className="ml-2">Sign in with Google</span>
          </Button>

          <Button
            size="big"
            type="button"
            className="justify-center"
            onClick={() => signInWithOAuth({ provider: "github" })}
          >
            <svg className="h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="ml-2">Sign in with GitHub</span>
          </Button>
        </div>

        <div className="my-6 flex items-center">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700/30" />
          <div className="mx-3 text-sm opacity-60">OR</div>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700/30" />
        </div>

        <form
          onSubmit={async (ev) => {
            ev.preventDefault()
            signIn({ email, password })
          }}
        >
          <InputLabel
            id="email"
            name="email"
            type="email"
            label="Email address"
            autoComplete="username"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            required
          />

          <div className="mt-6">
            <InputLabel
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              label={
                <div className="flex justify-between">
                  <span>Password</span>

                  <Link to="/auth/reset">Forgot your password?</Link>
                </div>
              }
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              required
            />
          </div>

          <Button loading={isLoading} variant="primary" size="big" type="submit" className="w-full justify-center mt-9">
            Sign in
          </Button>
        </form>
      </Card>
      <p className="mt-5 text-gray-700 dark:text-gray-400">
        Don&apos;t have an account? <Link to="/auth/registration">Sign up for a free account</Link>
      </p>
    </Container>
  )
}

export default LoginPage
